import { FormInstance, PaginationProps, notification } from "antd";
import { Dispatch, SetStateAction } from "react";
import moment from "moment-timezone";
import dayjs from "dayjs";

import { ActiveDatesObj, MetadataType, SortObj, VideoFilterObj } from "../videos/videos";
import { MetadataFilter } from "../metadata/hooks/useMetadataHook";
import { EpisodeFilterObj } from "../episode/hooks/useEpisodeHook";
import { authAxios, fetchWithAuthorization, getEmailFromSessionStorage } from "./session_utils";
import { USERDATA, USER_PAGE_SIZE } from "../../App";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import { getFileNameWithURL } from "../videos/modals/AddVideosMetaDataForm";
import { ThumbnailsFilterObj } from "../thumbnails/hooks/useThumbnailsHook";
import { videoFormFields } from "../videos/hooks/useVideosHooks";
import { AxiosRequestConfig } from "axios";

export const getDigitsOnly = (string: string, withPlusMinusSign?: boolean) => {
	const pattern = withPlusMinusSign ? /^(-?\d*\.?\d*)[\s\S]*$/ : /\D/g; // Matches any non-digit character
	const numbersOnly = string.replace(pattern, withPlusMinusSign ? "$1" : "");
	return numbersOnly;
};

/**
 * Formats an ID string to show first and last few digits with ellipsis in between
 * @param id - The ID string to format
 * @param prefixLength - Number of characters to show at the start (default: 4)
 * @param suffixLength - Number of characters to show at the end (default: 4)
 * @returns Formatted string like "1234...5678"
 */
export const formatIdShort = (id: string | null | undefined, prefixLength = 4, suffixLength = 4): string => {
	if (!id) return '';
	if (id.length <= prefixLength + suffixLength) return id;
	return `${id.substring(0, prefixLength)}...${id.substring(id.length - suffixLength)}`;
};

export const getDurationOnly = (string: string) => {
	const pattern = /[^0-9:.]+$/g; // Matches any non-digit character
	const durationOnly = string.replace(pattern, "");
	return durationOnly;
};

// This method will accept following formats:
// - "0:10:09"
// - "10:10"
// - "00:10:10.999"
// - "10:10.999"
// - "10.999"
// - "50"
// and will return a string as "hh:mm:ss.SSS"
export const durationCoverter = (duration: string) => {
	let hrs = "00",
		min = "00",
		sec = "00",
		ms = "000";
	if (!duration?.trim()) return `${hrs}:${min}:${sec}.${ms}`;
	const durationList = duration.split(":");
	if (durationList.length > 2) {
		[hrs, min, sec] = durationList;
	} else if (durationList.length > 1) {
		[min, sec] = durationList;
	} else {
		sec = durationList[0];
	}

	if (sec.includes(".")) {
		const [seconds, milliseconds] = sec.split(".");
		sec = seconds;
		ms = milliseconds || "000";
	}

	return `${hrs}:${min}:${sec}.${ms}`;
};

export const durationToMilliseconds = (duration: any) =>
	duration?.trim() ? moment.duration(durationCoverter(duration)).asMilliseconds() : 0;

const MIN_CUE_POINT_GAP_MS = 60 * 1000;

export type CuePointValidationResult = {
	valid: boolean;
	message?: string;
};

export const validateCuePointOrderAndSpacing = (cuePointsString: string): CuePointValidationResult => {
	if (!cuePointsString?.trim()) {
		return { valid: true };
	}

	try {
		const cueList = cuePointsString
			.split(",")
			.map(cuepoint => cuepoint.trim())
			.filter(Boolean);

		if (cueList.length <= 1) {
			return { valid: true };
		}

		const msValues = cueList.map(cuepoint => durationToMilliseconds(cuepoint));

		for (let i = 1; i < msValues.length; i++) {
			if (msValues[i] <= msValues[i - 1]) {
				return { valid: false, message: "Cue points must be in ascending order" };
			}
			if (msValues[i] - msValues[i - 1] < MIN_CUE_POINT_GAP_MS) {
				return { valid: false, message: "Cue points must be at least 1 minute apart" };
			}
		}

		return { valid: true };
	} catch (error) {
		return { valid: false, message: "Invalid cue points" };
	}
};

export interface LowerCaseKeyInterFace {
	[key: string]: any;
}
export const makeKeyInLowerCaseHelper = (data: any): LowerCaseKeyInterFace[] => {
	return data?.map((row: LowerCaseKeyInterFace, index: number) => {
		const lowecaseObject: LowerCaseKeyInterFace = {};
		Object.keys(row).forEach(key => {
			lowecaseObject[key.toLowerCase()] = row[key] ?? "";
			lowecaseObject["key"] = row['KEY'] ?? `${row["ID"]}-${index}`;
		});
		return lowecaseObject;
	}) ?? [];
};

export const getLanguageList = (setLanguageList: (value: any[]) => void) => {
	authAxios({
		method: "GET",
		url: `${process.env.REACT_APP_BACK_END_API}/snowflake/language`,
	})
		.then((response: any) => {
			const languageList = response.data.data.map((data: any) => {
				return { ...data, key: data.CODE };
			});
			setLanguageList(languageList);
		})
		.catch((err: any) => {
			setLanguageList([]);
			console.error("error while fetching language data", err);
		});
};

export const handleSelectedRowStyling = (selectedVideoKeys: string[], remove?: boolean) => {
	try {
		if (!remove) {
			selectedVideoKeys.forEach(key => {
				const selectedRowList = document.querySelectorAll(`[data-row-key="${key}"]`);
				const selectedRow = selectedRowList[0];
				selectedRow?.classList?.add("ant-table-row-selected");
			});
		} else {
			selectedVideoKeys.forEach(key => {
				const selectedRowList = document.querySelectorAll(`[data-row-key="${key}"]`);
				const selectedRow = selectedRowList[0];
				selectedRow?.classList?.remove("ant-table-row-selected");
			});
		}
	} catch (error) {
		console.error(error);
	}
};

export const getVideosData = async (
	setVideosData: Dispatch<SetStateAction<any[]>>,
	setIsLoading: Dispatch<SetStateAction<boolean>>,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	videosSortObj: SortObj,
	searchQuery: string,
	videoFilterObj?: VideoFilterObj
) => {
	return new Promise(resolve => {
		fetchWithAuthorization(
			`${process.env.REACT_APP_BACK_END_API}/snowflake/video?pagination=${JSON.stringify(
				pagination
			)}&sortObj=${JSON.stringify(videosSortObj ?? {})}&filter=${JSON.stringify(
				videoFilterObj ?? {}
			)}&searchQuery=${searchQuery}`
		)
			.then(res => res.json())
			.then(async ({ data, total }) => {
				const convertedObject = await makeKeyInLowerCaseHelper(data);
				setVideosData(convertedObject);
				setPagination({ ...pagination, total });
				setIsLoading(false);
				resolve("Done");
			})
			.catch(() => {
				setIsLoading(false);
				resolve("Error");
			});
	});
};

export const getVideoById = async (id: string) => {
	try {
		const config = {
			method: "get",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/video/${id}`,
		};
		const response = await authAxios(config);
		if (!response.data.err) {
			const convertedObject = await makeKeyInLowerCaseHelper(response.data.data);
			return convertedObject;
		}
	} catch (error) {
		console.error("getVideoById ~ error:", error);
		return;
	}
};

export const getTotalDataCount = (
	route: string,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	filterObj?: any,
	searchQuery?: string
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/${route}/total?filter=${JSON.stringify(
			filterObj ?? {}
		)}&searchQuery=${searchQuery}`
	)
		.then(res => res.json())
		.then(async ({ total }) => {
			setPagination({ ...pagination, total });
		});
};

export const getDataHandler = (
	setData: Dispatch<SetStateAction<any[]>>,
	setTotalData: Dispatch<SetStateAction<number>>,
	setIsLoading: Dispatch<SetStateAction<boolean>>,
	route: string,
	pagination?: PaginationProps,
	sortObj?: SortObj,
	filterObj?: VideoFilterObj
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/${route}?pagination=${JSON.stringify(
			pagination ?? {}
		)}&sortObj=${JSON.stringify(sortObj ?? {})}&filter=${JSON.stringify(filterObj ?? {})}`
	)
		.then(res => res.json())
		.then(async ({ rows, totalData }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(rows);
			setData(convertedObject);
			setTotalData(totalData);
			setIsLoading(false);
		})
		.catch(() => setIsLoading(false));
};

export const getWholeData = async (
	route: string,
	setData: Dispatch<SetStateAction<any[]>>,
	sortObj?: any,
	setLoading?: any
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/${route}?sortObj=${JSON.stringify(
			sortObj ?? {}
		)}`
	)
		.then(res => res.json())
		.then(async ({ rows }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(rows);
			setData(convertedObject);
			setLoading && setLoading(false);
		});
};

export const submitScheduleData = async (data: any,
	submitScheduleDataSuccess: Dispatch<SetStateAction<any>>,
	submitScheduleDataError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/process-schedule-data`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			submitScheduleDataSuccess(response.data);
		} else {
			submitScheduleDataError(response.data);
		}
	} catch (error) {
		console.error("submitScheduleData ~ error:", error);
		submitScheduleDataError({ error: error });
	}
}

export const cloneBlockSchedule = async (
	blockScheduleId: string,
	newTitle: string,
	onSuccess: (data: any) => void,
	onError: (error: any) => void,
	options?: { newStartDate?: string; newEndDate?: string; bringForward?: boolean }
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/clone-block-schedule`,
			headers: { "Content-Type": "application/json" },
			data: JSON.stringify({ blockScheduleId, newTitle, ...options }),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			onSuccess(response.data);
		} else {
			onError(response.data);
		}
	} catch (error) {
		console.error("cloneBlockSchedule ~ error:", error);
		onError({ error });
	}
};

export const saveBlockScheduleTitle = async (data: any,
	saveBlockScheduleTitleSuccess: Dispatch<SetStateAction<any>>,
	saveBlockScheduleTitleError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "PUT",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/update-block-schedule-title`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			saveBlockScheduleTitleSuccess(response.data);
		} else {
			saveBlockScheduleTitleError(response.data);
		}
	} catch (error) {
		console.error("saveBlockScheduleTitle ~ error:", error);
		saveBlockScheduleTitleError({ error: error });
	}
}

/**
 * Records the schedule the current user just picked as their last-used one, so
 * the scheduler reopens it on their next page load. Stored per user, so several
 * people can have the same schedule as their most recent without evicting each
 * other. Fire-and-forget: a failure here must never block the user from opening
 * the schedule they selected.
 */
export const touchBlockSchedule = async (blockScheduleId: string) => {
	try {
		await authAxios({
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/touch-block-schedule`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify({ blockScheduleId }),
		});
	} catch (error) {
		console.error("touchBlockSchedule ~ error:", error);
	}
}

/**
 * Permanently deletes a saved block schedule. Callers get the error payload so
 * they can distinguish "already deleted by someone else" (404) from a real
 * failure.
 */
export const deleteBlockSchedule = async (
	blockScheduleId: string,
	onSuccess: (data: any) => void,
	onError: (error: any) => void
) => {
	try {
		const config = {
			method: "DELETE",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/block-schedule/${encodeURIComponent(blockScheduleId)}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			onSuccess(response.data);
		} else {
			onError(response.data);
		}
	} catch (error) {
		console.error("deleteBlockSchedule ~ error:", error);
		onError({ error });
	}
}

export const loadBlockSchedules = async (
	loadBlockSchedulesSuccess: Dispatch<SetStateAction<any>>,
	loadBlockSchedulesError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/load-block-schedules`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			loadBlockSchedulesSuccess(response.data);
		} else {
			loadBlockSchedulesError(response.data);
		}
	} catch (error) {
		console.error("loadBlockSchedules ~ error:", error);
		loadBlockSchedulesError({ error: error });
	}
}

export const getEpisodeSelectionData = (
data: any, 
successCallback: (result: any) => void, errorCallback: (error: any) => void) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/find-episode-selections-v2`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		authAxios(config)
			.then((response: any) => {
				if (!response.data.error) {
					successCallback(response.data);
				} else {
					errorCallback(response.data);
				}
			})
			.catch((error: any) => {
				console.error("getEpisodeSelectionData ~ error:", error);
				errorCallback(error);
			});
	} catch (error) {
		console.error("getEpisodeSelectionData ~ error:", error);
		errorCallback(error);
	}
}

export interface MarathonSegmentIssue {
	refId: string;
	title: string | null;
	actual: number;
	totalSegments: number;
	videoCount: number;
	expectedMin: number | null;
	expectedMax: number | null;
	missingS3Urls: number;
	reasons: string[];
	videoId: string | null;
	fileName: string | null;
	url: string | null;
	segmentationStatus: string | null;
	segmentationNotes: string | null;
}

// Validate that every episode ref on the grid has a complete Marathon (feed 10)
// segment set. Marathon FAST only — see validate_marathon_segments.ts.
// distributionPlatformIds scopes the surfaced video to the grid's tagged asset.
export const validateMarathonSegments = (
	refIds: string[],
	distributionPlatformIds: string[],
	successCallback: (result: { issues: MarathonSegmentIssue[]; checked: number }) => void,
	errorCallback: (error: any) => void,
) => {
	try {
		authAxios({
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/validate-marathon-segments`,
			headers: { "Content-Type": "application/json" },
			data: JSON.stringify({ refIds, distributionPlatformIds }),
		})
			.then((response: any) => successCallback(response.data))
			.catch((error: any) => errorCallback(error));
	} catch (error) {
		errorCallback(error);
	}
};

export const getAmagiCloudPortMediaFeeds = (successCallback: (result: any[]) => void, errorCallback: (error: any) => void) => {
	try {
		const config = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/amagi-cloud-port-media-feeds`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		authAxios(config)
			.then((response: any) => {
				if (!response.data.error) {
					successCallback(response.data);
				} else {
					errorCallback(response.data);
				}
			})
			.catch((error: any) => {
				console.error("getAmagiCloudPortMediaFeeds ~ error:", error);
				errorCallback(error);
			});
	} catch (error) {
		console.error("getAmagiCloudPortMediaFeeds ~ error:", error);
		errorCallback(error);
	}
}

export const getDistributionPlatforms = (successCallback: (result: any[]) => void, errorCallback: (error: any) => void) => {
	try {
		const config = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/distribution-platform-options`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		authAxios(config)
			.then((response: any) => {
				if (!response.data.error) {
					successCallback(response.data);
				} else {
					errorCallback(response.data);
				}
			})
			.catch((error: any) => {
				console.error("getDistributionPlatforms ~ error:", error);
				errorCallback(error);
			});
	} catch (error) {
		console.error("getDistributionPlatforms ~ error:", error);
		errorCallback(error);
	}
};

export const loadBlockScheduleData = async(id: string,
	loadBlockScheduleDataSuccess: Dispatch<SetStateAction<any>>,
	loadBlockScheduleDataError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/load-block-schedule-data`,
			headers: {
				"Content-Type": "application/json",
			},
			params: {
				idOrTitle: id,
			},
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			loadBlockScheduleDataSuccess(response.data);
		} else {
			loadBlockScheduleDataError(response.data);
		}
	} catch (error) {
		console.error("loadBlockScheduleData ~ error:", error);
		loadBlockScheduleDataError({ error: error });
	}
}

export const saveBlockScheduleData = async (data: any,
	saveBlockScheduleDataSuccess: Dispatch<SetStateAction<any>>,
	saveBlockScheduleDataError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/save-block-schedule-data`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			saveBlockScheduleDataSuccess(response.data);
		} else {
			saveBlockScheduleDataError(response.data);
		}
	} catch (error) {
		console.error("saveBlockScheduleData ~ error:", error);
		saveBlockScheduleDataError({ error: error });
	}
}

export const updateBlockScheduleCalendarConfig = async (
	data: any,
	updateBlockScheduleCalendarConfigSuccess: Dispatch<SetStateAction<any>>,
	updateBlockScheduleCalendarConfigError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/update-block-schedule-config`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			updateBlockScheduleCalendarConfigSuccess(response.data);
		} else {
			updateBlockScheduleCalendarConfigError(response.data);
		}
	} catch (error) {
		console.error("updateBlockScheduleCalendarConfig ~ error:", error);
		updateBlockScheduleCalendarConfigError({ error: error });
	}
}

export const submitScheduleDataV2 = async (data: any,
	submitScheduleDataSuccess: Dispatch<SetStateAction<any>>,
	submitScheduleDataError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/v2/process-schedule-data`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			submitScheduleDataSuccess(response.data);
		} else {
			submitScheduleDataError(response.data);
		}
	} catch (error) {
		console.error("submitScheduleData ~ error:", error);
		submitScheduleDataError({ error: error });
	}
}

export const getCSVWithAds = async (data: any,
	getCSVWithAdsSuccess: (data: any, response?: any) => void,
	getCSVWithAdsError: Dispatch<SetStateAction<any>>
) => {
	try {
		const platform = data.selectedTargetPlatform;
		let endpoint = '';
		// Marathon FAST shares the Amagi FAST exporter (same rules; only the segment
		// feed and the column set differ), so it uses the same async endpoint.
		if(platform === 'Amagi FAST' || platform === 'Marathon FAST') endpoint = `${process.env.REACT_APP_BACK_END_API}/scheduling/process-amagi-fast`;
		if(platform === 'Amagi OTA') endpoint = `${process.env.REACT_APP_BACK_END_API}/scheduling/process-amagi-ota`;
		const config: AxiosRequestConfig = {
			method: "POST",
			url: endpoint,
			data: data,
		};
		const response = await authAxios(config);
		if (response?.data && !response.data.error) {
			getCSVWithAdsSuccess(response.data, response);
		} else {
			getCSVWithAdsError(response.data);
		}
	} catch (error) {
		console.error("Error processing CSV: ", error);
		getCSVWithAdsError({ error: error });
	}
}

export const getJobStatus = async (operationId: string) => {
	const response = await authAxios({
		method: "GET",
		url: `${process.env.REACT_APP_BACK_END_API}/api/jobs/${operationId}`,
	});
	return response.data;
}

export const getSegmentFeedDropDownData = async (
	setSegmentParentFeedDropDownData: Dispatch<SetStateAction<any[]>>,
	setIsParentFeedValuesLoaded: Dispatch<SetStateAction<boolean>>,
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/segment-parent-feed-options`
	)
		.then(res => res.json())
		.then(async ({ data }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(data);
			setSegmentParentFeedDropDownData(convertedObject);
			setIsParentFeedValuesLoaded && setIsParentFeedValuesLoaded(true);
		});
}


export const getSeriesSelectData = async (
	setSeriesSelectData: Dispatch<SetStateAction<any[]>>,
	setSeriesMap: Dispatch<SetStateAction<any>>,
	setIsSeriesValuesLoaded: Dispatch<SetStateAction<boolean>>,
	setIsLoading?: (value: boolean) => void
) => {
	setIsLoading && setIsLoading(true);
	fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/snowflake/series-select`)
		.then(res => res.json())
		.then(async ({ data }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(data);
			const seriesMap: any = {};
			convertedObject.forEach((series: any) => {
				seriesMap[series.id] = series.titles;
			});
			setSeriesMap(seriesMap);
			setSeriesSelectData(convertedObject);
			setIsSeriesValuesLoaded(true);
			setIsLoading && setIsLoading(false);
		})
		.catch(() => setIsLoading && setIsLoading(false));
};

export const getSeriesData = (
	setSeriesData: Dispatch<SetStateAction<any[]>>,
	setIsLoading?: (value: boolean) => void,
	sortObj?: any
) => {
	setIsLoading && setIsLoading(true);
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/series?sortObj=${JSON.stringify(
			sortObj ?? {}
		)}`
	)
		.then(res => res.json())
		.then(async ({ data }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(data);
			setSeriesData(convertedObject);
			setIsLoading && setIsLoading(false);
		})
		.catch(() => setIsLoading && setIsLoading(false));
};

export const getMetaData = (
	setMetaData: Dispatch<SetStateAction<any[]>>,
	setIsLoading: Dispatch<SetStateAction<boolean>>,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	metadataSortObj: SortObj,
	filterObj?: MetadataFilter
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/metadata?pagination=${JSON.stringify(
			pagination ?? {}
		)}&sortObj=${JSON.stringify(metadataSortObj ?? {})}&filter=${JSON.stringify(filterObj ?? {})}`
	)
		.then(res => res.json())
		.then(async ({ rows, totalRows }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(rows);
			setPagination({ ...pagination, total: totalRows });
			setMetaData(convertedObject);
			setIsLoading(false);
		})
		.catch(() => setIsLoading(false));
};

export enum HomeTabKeyEnum {
	BASE = "/",
	VIDEOS = "/video",
	METADATA = "/metadata",
	SERIES = "/series",
	ZYPE = "/zype",
	WURL = "/wurl",
	LANGUAGE = "/language",
	SETTINGS_LANGUAGE = "/settings/language",
	SETTINGS_USER = "/settings/user",
	VIDEO_EXPORT = "/export/video",
	ASSETS_EXPORT = "/export/asset",
	ZYPE_EXPORT = "/export/platforms/zype",
	WURL_EXPORT = "/export/platforms/wurl",
	VIDEOS_IMPORT = "/import/video",
	ASSETS_IMPORT = "/import/asset",
	TAGS = "/tag",
	EPISODE = "/episode",
	AUTO_PODDER = "/auto-podder",
	AUTO_PODDER_PRESEED = "/auto-podder/preseed",
	BATCH_JOBS = "/batch-jobs",
	BATCH_JOBS_PODDED_REVIEW = "/batch-jobs/podded-review",
	BATCH_JOBS_CALENDAR = "/batch-jobs/calendar",
	UPLOAD_PROGRAMS = "/upload-programs",
	PROGRAMS_LISTING = "/programs-listing",
	POST_LOG_PROCESSOR = "/post-log-processor",
	CREATE_EPG = "/scheduling/create-epg",
	SCHEDULER = "/scheduling/scheduler",
	SEGMENTS_LISTING = "/scheduling/segments",
	DISTRIBUTIONR_PLATFORM = "/distribution-platform",
	ASSET_MATCHING = "/asset-matching",
	VIDEO_LISTS = "/video-lists",
	STATIONS = "/stations",
	CAPTIONS = "/captions",
	CUEPOINTS = "/cuepoints",
	THUMBNAILS = "/thumbnails",
	PROMOS = "/promos",
	VIEWERSHIP_DATA = "/data_processing",
	VIEWERSHIP_UPLOADER = "/viewership_uploader",
	EPISODE_SCORING ="/scoring/episodes",
	PAYMENT_MANAGER= "/payment_manager",
	BUNDLE_SPOT_DATA="/bundle_spot_data",
	HORIZON_PDF_UPLOADER="/horizon_pdf_uploader",
	BTT_UPLOADER="/btt_uploader",
	AVAILABILITY_SCHEDULES="/availability-schedules",
	BOOKMARK="/bookmark",
	VIDEO_TRANSCRIPT='/video-transcript',
	ANALYTICS='/analytics',
	VALIDATIONS= '/validations',
	DEAL_MANAGER='/deal-manager',
	DEAL_MANAGER_CHANNEL='/deal-manager/channel',
	DEAL_MANAGER_TERRITORY='/deal-manager/territory',
	DEAL_MANAGER_PARTNER='/deal-manager/partner',
	YOUTUBE_EXPORT='/youtube-export',
	YOUTUBE_LIVE_STREAMS='/live-streams',
	YOUTUBE_LIVE_CONNECTIONS='/youtube-live-connections',
	PLAYOUT_SERVERS='/playout-servers',
	AS_PLAYED_REPORTING='/as-played-reporting',
	CHANNELS='/channels',
	BULK_DESCRIPTIONS='/bulk-descriptions',
	BUNDLE_POST_LOG_UPLOADER='/bundle-post-log-uploader',
	PROFANITY_CHECK='/profanity-check',
	VIDEO_COMPLIANCE_UPLOAD='/video-compliance-upload',
	TERMS_OF_USE='/terms-of-use',
	PRIVACY_POLICY='/privacy-policy',
	ADS_ADJUST='/ads-adjust',
	CSV_VALIDATION='/scheduling/csv-validation',
	MEDIA_PLAN_BUILDER='/media-plan-builder',
	ESTIMATE_UPLOADS='/estimate-uploads',
	AMAGI_INGEST='/amagi-ingest',
	SYNDICATION_REPORT_EXPORTER='/syndication-report-exporter',
	SENSITIVE_SEARCH='/sensitive-search',
	EPISODE_INDEX='/episode-index',
	PIPELINE_ADMIN='/pipeline-admin',
	TRAFFIC_INSTRUCTIONS='/traffic-instructions',
	ORDER_HIGHLIGHTER='/order-highlighter'
}

export const titleObj: any = {
	[HomeTabKeyEnum.BASE]: "Home",
	[HomeTabKeyEnum.VIDEOS]: "Videos",
	[HomeTabKeyEnum.METADATA]: "Metadata",
	[HomeTabKeyEnum.SERIES]: "Series",
	[HomeTabKeyEnum.ZYPE]: "Zype",
	[HomeTabKeyEnum.WURL]: "Wurl",
	[HomeTabKeyEnum.LANGUAGE]: "Language",
	[HomeTabKeyEnum.SETTINGS_LANGUAGE]: "Settings | Language",
	[HomeTabKeyEnum.SETTINGS_USER]: "Settings | User",
	[HomeTabKeyEnum.VIDEO_EXPORT]: "Export | Videos",
	[HomeTabKeyEnum.ASSETS_EXPORT]: "Export | Assets",
	[HomeTabKeyEnum.ZYPE_EXPORT]: "Platform Export | Zype",
	[HomeTabKeyEnum.WURL_EXPORT]: "Platform Export | Wurl",
	[HomeTabKeyEnum.VIDEOS_IMPORT]: "Import | Videos",
	[HomeTabKeyEnum.ASSETS_IMPORT]: "Import | Assets",
	[HomeTabKeyEnum.TAGS]: "Tags",
	[HomeTabKeyEnum.EPISODE]: "Episodes",
	[HomeTabKeyEnum.PROMOS]: "Promos",
	[HomeTabKeyEnum.AUTO_PODDER]: "Auto Podder",
	[HomeTabKeyEnum.AUTO_PODDER_PRESEED]: "Auto Podder | Pre-Seed",
	[HomeTabKeyEnum.UPLOAD_PROGRAMS]: "Upload Programs",
	[HomeTabKeyEnum.PROGRAMS_LISTING]: "Programs Listing",
	[HomeTabKeyEnum.CREATE_EPG]: "Create EPG",
	[HomeTabKeyEnum.SCHEDULER]: "Scheduler",
	[HomeTabKeyEnum.SEGMENTS_LISTING]: "Segments Listing",
	[HomeTabKeyEnum.POST_LOG_PROCESSOR]: "Post Log Processor",
	[HomeTabKeyEnum.BATCH_JOBS]: "Batch Jobs",
	[HomeTabKeyEnum.BATCH_JOBS_PODDED_REVIEW]: "Batch Jobs | Pod Results",
	[HomeTabKeyEnum.BATCH_JOBS_CALENDAR]: "Batch Jobs | Calendar View",
	[HomeTabKeyEnum.DISTRIBUTIONR_PLATFORM]: "Distribution Platform",
	[HomeTabKeyEnum.CAPTIONS]: "Captions",
	[HomeTabKeyEnum.CUEPOINTS]: "Cue Points",
	[HomeTabKeyEnum.THUMBNAILS]: "Thumbnails",
	[HomeTabKeyEnum.ASSET_MATCHING]: "Asset Matcing",
	[HomeTabKeyEnum.STATIONS]: "Stations",
	[HomeTabKeyEnum.VIDEO_LISTS]: "Video Lists",
	[HomeTabKeyEnum.VIEWERSHIP_DATA]: "Vewership Data",
	[HomeTabKeyEnum.VIEWERSHIP_UPLOADER]: "Viewership Uploader",
	[HomeTabKeyEnum.EPISODE_SCORING]: "Scheduling | Episode Scoring",
	[HomeTabKeyEnum.PAYMENT_MANAGER]: "Payment Manager",
	[HomeTabKeyEnum.AVAILABILITY_SCHEDULES]: "Availability Schedules",
	[HomeTabKeyEnum.BOOKMARK]: "Bookmark",
	[HomeTabKeyEnum.DEAL_MANAGER]: 'Deals',
	[HomeTabKeyEnum.DEAL_MANAGER_CHANNEL]: 'Deals | Channel',
	[HomeTabKeyEnum.DEAL_MANAGER_TERRITORY]: 'Deals | Territory',
	[HomeTabKeyEnum.DEAL_MANAGER_PARTNER]: 'Deals | Partner',
	[HomeTabKeyEnum.ANALYTICS]: 'Analytics',
	[HomeTabKeyEnum.VALIDATIONS]: 'Validations',
	[HomeTabKeyEnum.YOUTUBE_EXPORT]: 'YouTube Export',
	[HomeTabKeyEnum.YOUTUBE_LIVE_STREAMS]: 'YouTube Live Streams',
	[HomeTabKeyEnum.YOUTUBE_LIVE_CONNECTIONS]: 'YouTube Live | Connections',
	[HomeTabKeyEnum.PLAYOUT_SERVERS]: 'Playout Servers',
	[HomeTabKeyEnum.AS_PLAYED_REPORTING]: 'As Played Reporting',
	[HomeTabKeyEnum.CHANNELS]: 'Channels',
	[HomeTabKeyEnum.BULK_DESCRIPTIONS]: 'Bulk Description Generator',
	[HomeTabKeyEnum.BUNDLE_POST_LOG_UPLOADER]: 'Post Log Uploader',
	[HomeTabKeyEnum.PROFANITY_CHECK]: 'Profanity Check',
	[HomeTabKeyEnum.VIDEO_COMPLIANCE_UPLOAD]: 'Video Compliance | Upload Video',
	[HomeTabKeyEnum.TERMS_OF_USE]: 'Terms of Use',
	[HomeTabKeyEnum.PRIVACY_POLICY]: 'Privacy Policy',
	[HomeTabKeyEnum.ADS_ADJUST]: 'Ads Adjust',
	[HomeTabKeyEnum.CSV_VALIDATION]: 'CSV Validation',
	[HomeTabKeyEnum.MEDIA_PLAN_BUILDER]: 'Media Plan Builder',
	[HomeTabKeyEnum.ESTIMATE_UPLOADS]: 'Estimate Uploads',
	[HomeTabKeyEnum.AMAGI_INGEST]: 'Amagi Ingest',
	[HomeTabKeyEnum.TRAFFIC_INSTRUCTIONS]: 'Traffic Instructions',
	[HomeTabKeyEnum.ORDER_HIGHLIGHTER]: 'Order Highlighter',

};

export const getTagsData = async (
	setTagsData: Dispatch<SetStateAction<any[]>>,
	setIsLoading?: (value: boolean) => void
) => {
	try {
		setIsLoading && setIsLoading(true);
		const config = {
			method: "get",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/tags`,
		};

		const response = await authAxios(config);
		if (!response.data.err) {
			const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
			setTagsData(convertedObject);
			setIsLoading && setIsLoading(false);
		}
	} catch (err) {
		setIsLoading && setIsLoading(false);
		console.error("erro while fetching tags data", err);
	}
};

export const getAllMetadata = async (setAllMetadata: (data: any) => void) => {
	try {
		const config = {
			method: "get",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/metadata`,
		};
		const respones = await authAxios(config);

		if (!respones.data.err) {
			setAllMetadata(respones.data.rows);
			// setIsAddNewMetadata(!respones.data.rows.length);
		}
	} catch (err) {
		console.error(err);
	}
};

export const getEpisodeData = (
	setEpisodeData: Dispatch<SetStateAction<any[]>>,
	setIsLoading: Dispatch<SetStateAction<boolean>>,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	episodeSortObj: SortObj,
	searchQuery: string,
	filterObj?: EpisodeFilterObj
) => {
	fetchWithAuthorization(
		`${process.env.REACT_APP_BACK_END_API}/snowflake/episode?pagination=${JSON.stringify(
			pagination ?? {}
		)}&sortObj=${JSON.stringify(episodeSortObj ?? {})}&filter=${JSON.stringify(
			filterObj ?? {}
		)}&searchQuery=${searchQuery}`
	)
		.then(res => res.json())
		.then(async ({ rows, total }) => {
			const convertedObject = await makeKeyInLowerCaseHelper(rows);
			setPagination({ ...pagination, total });
			setEpisodeData(convertedObject);
			setIsLoading(false);
		})
		.catch(() => setIsLoading(false));
};

export const getEpisodeByRefId = (ref_id: string) => {
	return new Promise<{ episodeData: any }>((resolve, reject) => {
		try {
			fetchWithAuthorization(
				`${process.env.REACT_APP_BACK_END_API}/snowflake/episode?filter=${JSON.stringify({
					ref_id,
					showHiddenEpisodes: true,
				})}`
			)
				.then(res => res.json())
				.then(async ({ rows }) => {
					const convertedObject = await makeKeyInLowerCaseHelper(rows);
					resolve({ episodeData: convertedObject[0] });
				});
		} catch (err) {
			reject({ err });
		}
	});
};

//no-useless-escape
// export const emailRegEx = /^([A-Za-z0-9_+%\-\.])+\@([A-Za-z0-9_+%\-\.])+\.([A-Za-z]{2,4})$/;
export const emailRegEx = /^([A-Za-z0-9_+%.-])+@([A-Za-z0-9_%.-])+\.[A-Za-z]{2,4}$/;

export const checkIfValidEmail = (_: any, value: string) => {
	if (!value || value === "") {
		return Promise.reject("Email Address is required!");
	}
	if (!value.match(emailRegEx)) {
		return Promise.reject("Email address is not valid!");
	}
	return Promise.resolve();
};

export const resetVideoFormFields = (form: FormInstance) => {
	form.resetFields(videoFormFields);
};

export const saveUpdateVideoData = async (
	formArray: any,
	data: any,
	metadataEditId: string | null | undefined,
	episodeId: string | null | undefined,
	currentTagsData: any[],
	distributionCheckedList: CheckboxValueType[],
	videoEditId: string | null | undefined,
	activeDatesList: ActiveDatesObj[]
) => {
	try {
		const metadataArr: any = [];
		const {
			video_ref_id,
			video_language,
			video_tags,
			video_url,
			duration,
			video_size,
			video_file_name,
		} = data;

		formArray.forEach((element: any, index: number) => {
			const tempObj: any = {
				metadata_language: data[`${index}_metaLanguage`],
				metadata_title: data[`${index}_title`],
				metadata_clean_title: data[`${index}_clean_title`] ?? "",
				metadata_keywords: data[`${index}_keywords`] ?? "",
				metadata_description_100: data[`${index}_description_100`] ?? "",
				metadata_description_250: data[`${index}_description_250`],
				metadata_description_full: data[`${index}_description_full`] ?? "",
				gracenote_episodic_tms_id: data[`${index}_gracenote_episodic_tms_id`] ?? "",
			};
			if (metadataEditId) {
				tempObj["id"] = metadataEditId;
			}
			metadataArr.push(tempObj);
		});
		const bodyData: any = {
			videoData: {
				...data,
				ref_id: video_ref_id,
				language: video_language,
				episode_id: episodeId,
				url: video_url ?? "",
				bumper_link: "",
				duration: duration ? moment.duration(durationCoverter(duration)).asSeconds() : 0,
				metadata: metadataArr,
				tags: video_tags,
				size: video_size,
				file_name: video_file_name,
				currentTagsData,
				distribution_platforms_data: activeDatesList,
			},
		};
		if (videoEditId) {
			bodyData["videoData"]["id"] = videoEditId;
		}

		const config = {
			method: videoEditId ? "put" : "post",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/video`,
			headers: { "Content-Type": "application/json" },
			data: JSON.stringify(bodyData),
		};

		const response = await authAxios(config);
		return response;
	} catch (err) {
		console.error("error while saving videos data", err);
		return { data: { err } };
	}
};

export const videoEditHelper = (
	record: any,
	setDurationValue: Dispatch<SetStateAction<any>>,
	setCurrentTagsData: Dispatch<SetStateAction<any[]>>,
	form: FormInstance,
	setDistributionCheckedList: Dispatch<SetStateAction<CheckboxValueType[]>>,
	setActiveDatesList: Dispatch<SetStateAction<ActiveDatesObj[]>>
) => {
	const {
		ref_id,
		audience_language,
		language,
		dub_language,
		sub_language,
		url,
		duration,
		gracenote_episodic_tms_id,
		metadata_language,
		title,
		clean_title,
		keywords,
		description_100,
		description_250,
		meta_id,
		tag_ids,
		size,
		type,
		distribution_data,
		description_full,
		file_name,
		video_height,
		video_width,
		zype_category_values,
		list_ids,
		updated_at,
		created_at,
	} = record;

	const durationObj = moment.duration(duration, "seconds");
	const seconds = moment.utc(durationObj.asMilliseconds()).format("HH:mm:ss");
	setDurationValue(seconds);
	setCurrentTagsData(tag_ids?.split(",")?.filter((tag: any) => tag?.trim()?.length));
	const distributionIds = distribution_data.map((data: any) => data.platform_id);
	const activeDatesList = distribution_data.map(
		(data: any): ActiveDatesObj => ({
			platformId: data.platform_id,
			platformName: data.platform_name,
			startDate: data.start_date,
			endDate: data.end_date,
		})
	);
	setDistributionCheckedList(distributionIds);
	setActiveDatesList(activeDatesList);
	form.setFieldsValue({
		"video_ref_id": ref_id,
		audience_language,
		"video_language": language,
		"dub_language": dub_language?.length ? dub_language : undefined,
		"sub_language": sub_language?.length ? sub_language : undefined,
		"video_tags": tag_ids
			?.split(",")
			?.filter((tag: any) => tag?.trim()?.length)
			?.map((tag: any) => tag),
		"video_url": url,
		"duration": seconds,
		"meta_id": meta_id ?? undefined,
		"video_size": size,
		"video_type": type,
		"video_file_name": file_name ?? getFileNameWithURL(url),
		"video_lists": list_ids?.split(",")?.filter((list: any) => list?.trim()?.length) ?? [],
		video_height,
		video_width,
		zype_category_values,
		"video_updated_at": updated_at ?? created_at,
		"0_metaLanguage": metadata_language,
		"0_title": title,
		"0_clean_title": clean_title,
		"0_keywords": keywords,
		"0_description_100": description_100,
		"0_description_250": description_250,
		"0_description_full": description_full,
		"0_gracenote_episodic_tms_id": gracenote_episodic_tms_id,
	});
};

export const tagsFilterOptions = (inputValue: string, option: any) => {
	return option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
};

export const videoListfilterOptions = (inputValue: string, option: any) => {
	return option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
};

export const seriesFilterOptions = (inputValue: string, option: any) => {
	return option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
};

export const distributionPlatformFilterOptions = (inputValue: string, option: any) => {
	return option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
};

export const metadataSelectHandler = (
	value: string,
	form: FormInstance,
	setMetadataEditId: (value: any) => void,
	setIsAddNewMetadata: (value: boolean) => void,
	setFormArray: (value: any) => void,
	allMetadata?: MetadataType[]
) => {
	console.log("🚀 ~ metadataSelectHandler ~ value:", value, allMetadata)
	setIsAddNewMetadata(true);
	setFormArray(["1"]);
	if (value === "add_new") {
		form.resetFields([
			"0_metaLanguage",
			"0_title",
			"0_clean_title",
			"0_description_100",
			"0_description_250",
			"0_gracenote_episodic_tms_id",
		]);
		form.setFieldsValue({ "0_metaLanguage": form.getFieldValue("audience_language") });
		setMetadataEditId(undefined);
	} else {
		setMetadataEditId(value);
		allMetadata
			?.filter(metadata => metadata.ID === value)
			?.forEach(metadata => {
				const { CLEAN_TITLE, DESCRIPTION_100, DESCRIPTION_250, LANGUAGE, TITLE, GRACENOTE_EPISODIC_TMS_ID } = metadata;
				form.setFieldsValue({
					"0_metaLanguage": LANGUAGE,
					"0_title": TITLE,
					"0_clean_title": CLEAN_TITLE,
					"0_description_100": DESCRIPTION_100,
					"0_description_250": DESCRIPTION_250,
					"0_gracenote_episodic_tms_id": GRACENOTE_EPISODIC_TMS_ID,
				});
			});
	}
};

export type ExportApiData = {
	series?: string[];
	distribution_platforms?: string[];
	audience_language?: string[];
	video_tags?: string[];
	video_not_tagged?: string[];
	episode_tags?: string[];
	episode_not_tagged?: string[];
	include_distribution_platforms?: boolean;
	ref_id?: string;
};

type ExportType =
	| "series"
	| "videos"
	| "assets"
	| "assets/captions"
	| "assets/cuepoints"
	| "assets/thumbnails"
	| "platforms/zype"
	| "platforms/wurl"
	| "promos";

export const exportCSVHandler = async (
	data: ExportApiData,
	message: string,
	description: string,
	exportType: ExportType
) => {
	try {
		notification.info({
			message,
			description,
		});
		const email = getEmailFromSessionStorage();
		const response: any = await authAxios({
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API
				}/snowflake/export/csv/${exportType}?data=${JSON.stringify({ ...data, email })}`,
			responseType: "blob",
		});

		if (response.status === 200) {
			const headers: any = response.headers;
			const contentDisposition = headers?.get("content-disposition");
			const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
			const filename = filenameMatch ? filenameMatch[1] : "exports.csv";
			const href = URL.createObjectURL(response.data);
			const a = document.createElement("a");
			a.href = href;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(href);
			document.body.removeChild(a);
			notification.success({
				message: "Export",
				description: "CSV file has been downloaded. Please check in downloads.",
			});
		} else {
			notification.error({
				message: "Export",
				description: "CSV file couldn't be downloaded. Please try again.",
			});
			throw new Error("Export job could not be started.");
		}
	} catch (error) {
		console.error(error);
	}
};

export const exportCSVWithLambda = async (
	data: any,
	assetType: ExportType,
	name: string,
	message?: string
) => {
	try {
		const email = getEmailFromSessionStorage();		
		const response: any = await authAxios({
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API
				}/snowflake/export/csv/${assetType}?data=${JSON.stringify({ ...data, email, name })}`,
		});
		const { selectedIds, selectedEpisodeIds, selectedVideoIds } = data;
		const selectedItemsCount = selectedIds?.length ?? selectedEpisodeIds?.length ?? selectedVideoIds?.length ?? 0;
		const notificationDesc =
			response.status === 200
				? (selectedItemsCount ? `Exporting ONLY ${selectedItemsCount} selected records.  You'll be emailed a CSV file once it's ready` : "You'll be emailed a CSV file once it's ready.")
				: "Export job couldn't be started. Please try again.";
		notification.success({
			message: message ?? "Export",
			description: notificationDesc,
		});
	} catch (error) {
		console.error("error in export csv with lambda", error);
		throw new Error("export csv error");
	}
};

export const searchHandler = (
	form: FormInstance,
	timeoutId: NodeJS.Timeout | undefined,
	setTimeoutId: Dispatch<SetStateAction<NodeJS.Timeout | undefined>>,
	setSearchQuery: Dispatch<SetStateAction<string>>
) => {
	try {
		clearTimeout(timeoutId);
		const newTimeoutId = setTimeout(async () => {
			const searchText = await form.getFieldValue("search");
			setSearchQuery(searchText?.trim());
		}, 1000);

		setTimeoutId(newTimeoutId);
	} catch (err) {
		console.error({ err });
	}
};

export interface SETTINGS_DATA {
	user_id: string;
	show_public_lists?: boolean;
	page_sizes: USER_PAGE_SIZE;
}

export const updateUserPageSizeSettings = async (settingsData: SETTINGS_DATA) => {
	try {
		const config: any = {
			method: "PUT",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/settings/updatePageSizesData`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify({ userId: settingsData.user_id, pageSizes: settingsData.page_sizes }),
		};
		authAxios(config);
	} catch (err) {
		console.error({ err });
	}
};

export const getUserData = async (
	email: string,
	setUserData: Dispatch<SetStateAction<USERDATA | undefined>>
) => {
	try {
		const config: any = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/user/${email}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		const convertedObject = await makeKeyInLowerCaseHelper([response.data]);
		const pageSizes = convertedObject[0]["page_sizes"]
			? JSON.parse(convertedObject[0]["page_sizes"])
			: {};
		setUserData({ ...convertedObject[0], page_sizes: pageSizes } as unknown as USERDATA);
		return convertedObject[0];
	} catch (err) {
		console.error({ err });
	}
};

export const getUserPageSizes = async (
	userId: string,
	setPageSizeUserSettings: Dispatch<SetStateAction<USER_PAGE_SIZE | undefined>>
) => {
	try {
		const config: any = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/settings/${userId}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		const convertedObject = await makeKeyInLowerCaseHelper([response.data]);
		const pageSizes = convertedObject[0]["page_sizes"]
			? JSON.parse(convertedObject[0]["page_sizes"])
			: {};
		setPageSizeUserSettings(pageSizes as unknown as USER_PAGE_SIZE);
		return convertedObject[0];
	} catch (err) {
		console.error({ err });
	}
};

export const updateHiddenEpisodesHandler = async (userId: string, showHiddenEpisodes: boolean) => {
	try {
		const config = {
			method: "PUT",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/settings/updateHiddenEpisodes`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify({ userId: userId, showHiddenEpisodes: !showHiddenEpisodes }),
		};
		authAxios(config);
	} catch (error) {
		console.error({ error });
	}
};

export const getCuePointsData = async (
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	setCuePointssData: Dispatch<SetStateAction<any>>,
	setLoading: Dispatch<SetStateAction<boolean>>,
	fileterObj: any,
	episode_id?: string | null
) => {
	try {
		const config: any = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/cuepoints${episode_id ? "/" + episode_id : ""
				}?filter=${fileterObj ? JSON.stringify(fileterObj) : ""}&pagination=${pagination ? JSON.stringify(pagination) : ""
				}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		setPagination({ ...pagination, total: response.data.totalCuepoints })
		const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
		setCuePointssData(convertedObject);
		setLoading(false);
	} catch (error) {
		console.error({ error });
		setLoading(false);
	}
};

export const getThumbnailsData = async (
	setThumbnailsData: Dispatch<SetStateAction<any>>,
	setLoading: Dispatch<SetStateAction<boolean>>,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	thumbnailsFilterObj?: ThumbnailsFilterObj,
	episode_id?: string | null
) => {
	try {
		// if episode is available just pass the episode id, else pass other filteres and pagination
		const url = `${process.env.REACT_APP_BACK_END_API}/snowflake/thumbnails${episode_id
			? "/" + episode_id
			: `?pagination=${JSON.stringify(pagination)}&filter=${JSON.stringify(
				thumbnailsFilterObj ?? {}
			)}`
			}`;
		const config: any = {
			method: "GET",
			url,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
		setPagination({ ...pagination, total: response.data.totalThumbnails })
		setThumbnailsData(convertedObject);
		setLoading(false);
	} catch (error) {
		console.error({ error });
	}
};

export const getBatchJobResultSheetTitle = async (
	spreadSheetId: string,
	jobId: string,
	setLoading: Dispatch<SetStateAction<boolean>>,
	setCurrentSheetMetaData: Dispatch<SetStateAction<any>>
) => {
	try {
		setLoading(true);
		const config: any = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/result-sheet-metadata?spreadSheetId=${spreadSheetId}&jobId=${jobId}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		console.log("getBatchJobResultSheetTitle ~ response:", response.data);
		if (response.data?.title) {
			setCurrentSheetMetaData({spreadSheetId: spreadSheetId, jobId: jobId, title: response.data.title});
		} else {
			setCurrentSheetMetaData({spreadSheetId: spreadSheetId, jobId: jobId, title: ""});
		}
		setLoading(false);
	}
	catch (error) {
		console.error({ error });
		setLoading(false);
	}
}

export const getBatchJobsData = async (
	setBatchJobsData: Dispatch<SetStateAction<any>>,
	setLoading: Dispatch<SetStateAction<boolean>>,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	pagination: PaginationProps
) => {
	try {
		const url = `${process.env.REACT_APP_BACK_END_API
			}/snowflake/batch-jobs?pagination=${JSON.stringify(pagination)}`;
		const config: any = {
			method: "GET",
			url,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
		console.log("getBatchJobsData ~ response:", convertedObject);
		setBatchJobsData(convertedObject);
		setPagination({ ...pagination, total: response.data.total })
		setLoading(false);
	} catch (error) {
		console.error({ error });
	}
};

export const getSegmentsData = async (
	setSegmentsData: Dispatch<SetStateAction<any>>,
	setLoading: Dispatch<SetStateAction<boolean>>,
	pagination: PaginationProps,
	setPagination: Dispatch<SetStateAction<PaginationProps>>,
	filterObj?: any,
	onError?: (error: any) => void,
) => {
	try {
		const url = `${process.env.REACT_APP_BACK_END_API}/snowflake/segments?pagination=${
			JSON.stringify(pagination)
		}&filter=${
			filterObj ? JSON.stringify(filterObj) : ""
		}`;
		const config: any = {
			method: "GET",
			url,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		const convertedObject = await makeKeyInLowerCaseHelper(response.data.rows);
		setPagination({ ...pagination, total: response.data.total })
		setSegmentsData(convertedObject);
		setLoading(false);
	} catch (error) {
		console.error({ error });
		setLoading(false);
		// Let callers react to a failed fetch instead of silently rendering empty.
		onError?.(error);
	}
}

export interface PlatformCaptionLanguagesData {
	orderedLanguages: string[];
	savedPriority: string[];
	combos: { languages: string[]; videos: number }[];
	totalVideos: number;
	videosWithoutCaptions: number;
}

export const getPlatformCaptionLanguages = async (
	platformId: string,
): Promise<PlatformCaptionLanguagesData> => {
	const config: any = {
		method: "GET",
		url: `${process.env.REACT_APP_BACK_END_API}/snowflake/distribution-platform/${platformId}/caption-languages`,
		headers: {
			"Content-Type": "application/json",
		},
	};
	const response = await authAxios(config);
	return response.data;
};

export const createSegmentData = async (
	segmentData: any,
	submitSuccess: Dispatch<SetStateAction<any>>,
	submitError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config: any = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(segmentData),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			submitSuccess(response.data);
		} else {
			submitError(response.data);
		}
	} catch (error) {
		submitError({ error });
		console.error({ error });
	}
}

export const updateSegmentData = async (
	segmentData: any,
	submitSuccess: Dispatch<SetStateAction<any>>,
	submitError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config: any = {
			method: "PUT",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(segmentData),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			submitSuccess(response.data);
		} else {
			submitError(response.data);
		}
	} catch (error) {
		submitError({ error });
		console.error({ error });
	}
}

export const deleteSegmentData = async (
	segmentId: string,
	submitSuccess: Dispatch<SetStateAction<any>>,
	submitError: Dispatch<SetStateAction<any>>
) => {
	try {
		const config: any = {
			method: "DELETE",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments/${segmentId}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			submitSuccess(response.data);
		} else {
			submitError(response.data);
		}
	} catch (error) {
		submitError({ error });
		console.error({ error });
	}
}

export const getBucketKeyWithURL = (
	videoURL: string
): { Key?: string; Bucket?: string; type?: string } => {
	if (videoURL?.trim()?.includes("s3://")) {
		// eslint-disable-next-line
		const [_, url] = videoURL.split("//");
		if (url) {
			let videoType = "";
			const [bucket, ...key] = url.split("/");
			const fileKey = key.join("/");
			if (fileKey) {
				const tempArr = fileKey.split(".");
				videoType = tempArr[tempArr.length - 1];
			}
			if (videoType) {
				return { Key: fileKey, Bucket: bucket, type: videoType };
			}
		}
	} else if (videoURL?.trim()?.includes("http://") || videoURL?.trim()?.includes("https://")) {
		const decodedURL = decodeURIComponent(videoURL.replace(/\+/g, " "));
		// eslint-disable-next-line
		const [_, url] = decodedURL.split("//");
		const [bucket, fileKey] = url.split(".s3.amazonaws.com/");
		let videoType = "";
		if (fileKey) {
			const tempArr = fileKey.split(".");
			videoType = tempArr[tempArr.length - 1];
		}
		return { Key: fileKey, Bucket: bucket, type: videoType };
	}
	return {};
};

export const getSignedURL = async (videoURL: string) => {
	try {
		const { Key, Bucket } = getBucketKeyWithURL(videoURL);
		const config: any = {
			method: "GET",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/video/signed-url?Key=${Key}&Bucket=${Bucket}`,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		return response.data.url;
	} catch (error) {
		console.error({ error });
		return null;
	}
};

export const getUploadSignedURL = async (contentType: string, fileName: string) => {
	try {
		const config: any = {
			method: 'GET',
			url: `${process.env.REACT_APP_BACK_END_API}/register-data/generate-presigned-url`,
			params: {
				contentType,
				fileName,
			},
			headers: {
				'Content-Type': 'application/json',
			},
		};
		const response = await authAxios(config);
		return response.data;
	} catch (error) {
		console.error({ error });
		return null;
	}
};


export const copyString = async (value: string) => {
	if (value && navigator) {
		await navigator.clipboard.writeText(value);
		notification.success({
			message: undefined,
			description: "Copied Successfully",
			placement: "bottomLeft",
		});
	}
};

export const getVideoDataByGracenoteId = async (gracenoteId: string) => {
	const config: any = {
		method: "GET",
		url: `${process.env.REACT_APP_BACK_END_API}/snowflake/video/gracenote/${gracenoteId}`,
		headers: {
			"Content-Type": "application/json",
		},
	};
	return authAxios(config);
};

// const updateData = (currentData: any, field: string, newData: any) => {};

export const updateDataWithGracenoteData = (form: FormInstance, gracenoteData: any, index: number) => {
	const { description, episode, original_air_date, season, title, videos } = gracenoteData;
	const updatedGracenoteData: any = {
		episode,
		season,
		original_air_date,
		[`${index}_title`]: title,
		[`${index}_description_full`]: description,
		"video_url": videos?.[0]?.url ?? "",
	};
	const formFields = [
		"season",
		"episode",
		"original_air_date",
		`${index}_title`,
		`${index}_description_full`,
		"video_url",
	];
	const currentData = form.getFieldsValue(formFields);
	const updatedFormData: any = {};
	formFields.forEach((field: string) => {
		updatedFormData[field] =
			currentData[field] && String(currentData[field]).length
				? currentData[field]
				: field === "original_air_date"
					? dayjs(updatedGracenoteData[field])
					: updatedGracenoteData[field];
	});
	form.setFieldsValue(updatedFormData);
};


export const FALLBACK_IMAGE = "data:image/png;base64,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";
